import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export function useArtisanProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["artisan-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("artisan_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useUpdateAvailability() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (availability: string) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("artisan_profiles")
        .update({ availability } as any)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artisan-profile"] });
    },
  });
}

/**
 * Returns whether the current user is an artisan.
 * Derived from useArtisanProfile so it shares the same cached query
 * and does not fire a separate DB request per component.
 */
export function useIsArtisan() {
  const { data, isLoading, ...rest } = useArtisanProfile();
  return { data: data !== null && data !== undefined, isLoading, ...rest };
}
